// The widget React root.
//
// Modes (top level):
//   - booking — the engaged booking flow (lead capture → strip + revealed panel)
//   - confirm-appointment — intermediate review screen before the SF write
//   - congrats — post-write confirmation + Get Set Up upsell form
//   - set-up — final "You're Set Up" acknowledgment
//
// Container-width responsive layout (ResizeObserver-driven, NOT viewport).
// Below 900px container width, the CSS collapses the strip + panel grids
// to single column. State machine + behavior are identical.

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import styles from './styles/widget.module.css';
import { ENGAGE_EVENT, emit } from './state/events';
import { reducer, initialState } from './state/machine';
import { collectsPhoneOnConfirm, countryConfigOrDefault, marketingConsentCombined, waitlistOffered, type CountryConfig } from './config/countries';
import { bookingEditFromConfirmActions, type BookingEditField } from './utils/bookingFlow';
import { useContainerWidth } from './utils/useContainerWidth';
import { parsePrice } from './utils/pricing';
import { pricingLabelFor, FITTING_TYPES, visibleFittingTypes } from './data/fittingTypes';
import { resolvePromoPricing } from './data/promoPricing';
import { getWebPromoCode } from './utils/webPromo';
import { postLead, seedAccount } from './analytics/lead-post';
import { useBookingDetailsReport } from './analytics/useBookingDetailsReport';
import {
  createBooking,
  getMasterPromoCodes,
  createWaitlist,
  listStoreFittings,
  listLocations,
  listResources,
  lookupAppointments,
  rescheduleBooking,
  validatePromo,
  ApiError,
  apiErrorMessage,
  doubleBookingMessage,
  gatewayTimeoutMessage,
  doubleBookingAfterTimeoutMessage,
} from './api/client';
import { buildFittingNameMap, buildFittingPriceMap, listPriceFor, resolveWireName, stripCountrySuffix } from './data/fittingNames';
import { addMinutesToFakeZ } from './utils/datetime';
import { formatPhoneFromInput, phoneForBookingApi } from './utils/phone';
import { isReadOnly } from './utils/readOnly';
import { emitAnalytics } from './analytics/dispatch';
import { getAttribution } from './analytics/attribution';
import { buildPromoCodeForSchedule } from './data/promoTerms';
import { captureMarketing } from './utils/marketing';
import { readIdentity, writeIdentity, matchStoredStore } from './utils/identity';
import type {
  BookingPayload,
  MarketingPayload,
  Location,
  Resource,
  LookedUpAppointment,
  ReschedulePayload,
} from './types';
import type { SelectedSlot } from './state/machine';

import { LeadCapture } from './steps/LeadCapture';
import { RescheduleAppointmentPicker } from './steps/RescheduleAppointmentPicker';
import { BookingStripPanel } from './steps/BookingStripPanel';
import { ConfirmAppointment } from './steps/ConfirmAppointment';
import { BookingConfirmed } from './steps/BookingConfirmed';
import { GetSetUp } from './steps/GetSetUp';
import { TrafficDrawer } from './components/TrafficDrawer';
import { Modal } from './components/Modal';
import { StepProgress } from './components/StepProgress';

interface WidgetProps {
  embedSource: string;
  /**
   * Inline landing-page mode. When true the widget renders Step 1 (lead
   * capture) in-page as a compact card — the right column of a standalone
   * landing page — and promotes to the existing full-screen modal once the
   * user reaches Step 2. Default false = the standard button-triggered modal
   * embed used on clubchampion.com, which is left completely unchanged.
   */
  inline?: boolean;
  /**
   * Phone "flavour" (orthogonal to inline/modal layout). When true the phone
   * number is collected on Step 1 as a required field (with the SMS-consent
   * opt-in moved up alongside it), and the last-step Get Set Up form drops its
   * phone + SMS controls. Default false = production behavior: phone is
   * optional and collected on the last step for the pre-fit call.
   */
  phoneMandatory?: boolean;
  /**
   * Which inline presentation to render (inline mounts only). 'classic'
   * (default) is the original landing-page card, byte-for-byte unchanged.
   * 'card' is the compact hero seed card: kicker + heading, Name/Email,
   * a YOUR STUDIO row, a FITTING TYPE row and a live NEXT AVAILABLE strip.
   */
  inlineVariant?: 'classic' | 'card';
  /**
   * Canonical fitting name (SF wire spelling, e.g. "Full Bag") to preselect
   * at mount. Matched case-insensitively against the fittings visible for the
   * mount's market; an unknown name preselects nothing rather than guessing.
   */
  presetFittingName?: string;
  /** The seed card's heading. Defaults to "Book a fitting near you". */
  cardHeading?: string;
  /**
   * Render the seed card in two columns: identity across the top, then the
   * booking context (studio / fitting / fitter) beside the slot, consent and
   * CTA. For a wide slot - a location or fitting detail page - where the single
   * column runs tall enough to push the CTA below the fold.
   */
  cardWide?: boolean;
  /**
   * Drop the fitting-type row, leaving Name, Email and Studio. The fitting is
   * chosen later in the flow instead.
   */
  cardBasic?: boolean;
  /**
   * Force a specific studio at mount — the location-context override. On a
   * store detail page the booking is FOR that studio, so this preselects it and
   * takes precedence over the visitor's favourite-store hint (cc:identity),
   * which still applies in every other context. Matched tolerantly against the
   * market's location list (see matchStoredStore); an unknown name preselects
   * nothing rather than falling back to the favourite.
   */
  presetStoreName?: string;
  /**
   * Preselect a fitter at mount (location-context). Resolved by name against
   * the forced studio's resource list once the studio is set; an unknown name
   * leaves the "Any Fitter" default. Only meaningful alongside presetStoreName.
   */
  presetFitterName?: string;
  /**
   * Boot straight into reschedule mode, prefilled. Set by an inline mount
   * carrying data-cc-mode="reschedule" (see main.tsx) — the manage-appointment
   * page's path: it has already looked the customer up via /bookings/lookup,
   * so the widget enters reschedule mode with the identity prefilled and runs
   * the lookup immediately, landing on the confirm screen (or the picker when
   * several appointments match and no target is named). `appointment` narrows
   * a multi-match to that appointment number so the picker is skipped.
   */
  rescheduleBoot?: { name: string; email: string; appointment?: string } | null;
  /**
   * Market for an INLINE mount, resolved by main.tsx from the container's
   * `data-cc-countrycode`. The modal receives its market per open on the engage
   * event; an inline instance never engages, so without this prop it booked
   * every market against the US store list and US pricing (Dan Parks,
   * 2026-10-17: an AU inline opened the USA booking). Null = US default.
   */
  country?: CountryConfig | null;
  /**
   * `data-cc-promoverride` for an inline mount — the same absolute override
   * the modal reads off its trigger. Wins over country.initialPromoCode.
   */
  promoOverride?: string | null;
}

type WidgetMode =
  | 'booking'
  | 'confirm-appointment'
  | 'congrats'

const DESKTOP_BREAKPOINT = 1024;

// Page-global click → book_flow_click tracking. This is a document-level
// listener for ANY `[data-cc-track]` element on the page, so it must attach
// EXACTLY ONCE regardless of how many Widget instances mount (N instances
// would otherwise fire book_flow_click N times per click). Guarded by a
// module-level flag and attached for the lifetime of the script load — never
// removed — so it survives individual instances unmounting.
let globalClickTrackingAttached = false;
function ensureGlobalClickTracking(): void {
  if (globalClickTrackingAttached) return;
  if (typeof document === 'undefined') return;
  globalClickTrackingAttached = true;
  document.addEventListener(
    'click',
    (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tracked = target.closest('[data-cc-track]') as HTMLElement | null;
      if (!tracked) return;
      const contentType = tracked.getAttribute('data-cc-track') ?? 'unknown';
      const contentId = tracked.getAttribute('data-cc-track-id') ?? undefined;
      const linkText = tracked.textContent?.trim().replace(/\s+/g, ' ') || undefined;
      const linkUrl = (tracked as HTMLAnchorElement).href || undefined;
      emitAnalytics({
        event: 'book_flow_click',
        content_type: contentType,
        content_id: contentId,
        link_text: linkText,
        link_url: linkUrl,
      });
    },
    true,
  );
}

// The dev-only TrafficDrawer (the `</>` debug affordance) must render at most
// once per page, not once per Widget instance — a multi-instance page (two
// inline sections + the modal) would otherwise stack several. The first
// instance to render claims ownership for the script's lifetime.
let trafficDrawerOwner: object | null = null;



export function Widget({
  embedSource,
  inline = false,
  phoneMandatory = false,
  inlineVariant = 'classic',
  presetFittingName,
  cardHeading,
  cardWide,
  cardBasic,
  presetStoreName,
  presetFitterName,
  rescheduleBoot = null,
  country: mountCountry = null,
  promoOverride: mountPromoOverride = null,
}: WidgetProps) {
  // Inline mode starts already showing the booking flow (Step 1 card) rather
  // than the idle/null state the button-triggered embed waits in.
  const [mode, setMode] = useState<WidgetMode | null>(inline ? 'booking' : null);
  // Phone flavour is fixed-per-container for inline instances (from props), but
  // resolved PER-OPEN for the shared modal instance: each engage event carries
  // the clicked trigger's own flavour, which we hold here. Defaults to the
  // prop, so a single-trigger page behaves identically before any click.
  const [phoneMandatoryState, setPhoneMandatoryState] = useState(phoneMandatory);
  // An inline mount's market is known before first paint, so it goes into the
  // initial state through the SET_COUNTRY reducer case (which also applies the
  // market's consent defaults). Dispatching it from an effect was too late: the
  // studio field had already requested the US store list on the first render.
  const [state, dispatch] = useReducer(reducer, initialState, (s) =>
    inline ? reducer(s, { type: 'SET_COUNTRY', payload: mountCountry?.code ?? null }) : s,
  );
  const [leadCaptureDone, setLeadCaptureDone] = useState(false);
  const [holdExpired, setHoldExpired] = useState(false);
  // Location-context open (a store/fitter CTA): the modal presents the compact
  // seed card as its Step 1 and, once the studio has resolved and we already
  // know who the visitor is, fast-forwards past Step 1 onto Step 2 with the
  // fitter preselected. Set per engage; the fast-forward decision is made once
  // per open (fastForwardDecided).
  const [ctxSeedCard, setCtxSeedCard] = useState(false);
  const fastForwardDecided = useRef(false);

  // The mount's preset fitting (`data-cc-fitting`), applied once per
  // LOCATION rather than once per mount or on every null.
  //
  // Both simpler policies shipped and both were wrong. Once-per-mount lost
  // the preset the moment a studio was chosen, because SELECT_LOCATION
  // clears the fitting. Re-apply-on-any-null then made the fitting
  // unchangeable: FittingTypeField's own Change control clears the selection
  // to open the picker, and the effect instantly re-selected the preset —
  // reported as "it just flickers, I cannot change the fitting type".
  //
  // Keying the application on the location id separates the two null
  // sources: a location change resets the key and re-applies the mount's
  // intent; a user's clear happens under an unchanged key and sticks.
  //
  // And once the customer has explicitly chosen a DIFFERENT fitting, the
  // preset stands down for good — re-imposing the page's fitting after a
  // later studio change would overwrite a decision the customer already
  // made, which read as "it keeps resetting to the parent page's fitting".
  // The site-local identity hint (cc:identity — Step 1 writes it, the My
  // Account handoff writes it) prefills what the visitor has already told
  // us: name, email, and their studio. Prefill only into EMPTY fields — a
  // value the customer typed this session always wins over a stored hint —
  // and the studio only resolves when it still exists in this market's list.
  const leadPrefilled = useRef(false);
  useEffect(() => {
    if (leadPrefilled.current) return;
    leadPrefilled.current = true;
    const id = readIdentity();
    if (!id) return;
    const lead: Record<string, string> = {};
    if (id.fullName && !state.fullName) lead.fullName = id.fullName;
    if (id.email && !state.email) lead.email = id.email;
    if (Object.keys(lead).length) dispatch({ type: 'SET_LEAD', payload: lead });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolving the stored studio is a SEPARATE effect keyed on the market's
  // country, because the studio only makes sense against that market's location
  // list — and on mount `state.country` is still null (the engage event sets it
  // a tick later). Resolving on mount fetched the wrong list and, worse, matched
  // by exact name: the hint's "Chicago Studio" never equalled the API's
  // "Chicago", so the studio silently failed to preselect. Now it waits for the
  // country, matches tolerantly (see matchStoredStore), and applies once.
  const storePrefilled = useRef(false);
  useEffect(() => {
    if (storePrefilled.current) return;
    // The customer choosing a studio ends the question for good.
    if (state.selectedLocation) {
      storePrefilled.current = true;
      return;
    }
    // Location-context override: a mount that names a studio (the store detail
    // page) is booking FOR that studio, so its name wins over the favourite-
    // store hint. When no studio is forced, fall back to the hint as before.
    const forced = presetStoreName?.trim();
    const id = forced ? null : readIdentity();
    if (!forced && (!id || (!id.storeId && !id.storeName))) {
      storePrefilled.current = true;
      return;
    }
    let cancelled = false;
    listLocations(state.country)
      .then((locs) => {
        if (cancelled) return;
        const match = matchStoredStore(locs, forced ? { storeName: forced } : id!);
        if (match) {
          storePrefilled.current = true;
          dispatch({ type: 'SELECT_LOCATION', payload: match });
        } else if (forced) {
          // A forced studio that doesn't resolve must NOT fall through to the
          // favourite — the page asked for this specific one. Stand down and
          // leave the field to the normal chooser.
          storePrefilled.current = true;
        }
      })
      .catch(() => {
        // The hint stays a hint; the studio field just starts empty.
      });
    return () => {
      cancelled = true;
    };
  }, [state.country, state.selectedLocation, presetStoreName]);

  // Location-context fitter preselect. Once the forced studio has resolved, look
  // the named fitter up in that studio's resource list and preselect it; an
  // unknown name leaves the "Any Fitter" default untouched. Applied once, and
  // never against a fitter the customer has already chosen this session.
  const fitterPrefilled = useRef(false);
  useEffect(() => {
    if (fitterPrefilled.current) return;
    const wanted = presetFitterName?.trim().toLowerCase();
    if (!wanted) return;
    if (state.selectedFitterId) {
      fitterPrefilled.current = true;
      return;
    }
    const loc = state.selectedLocation;
    if (!loc) return; // wait for the studio to resolve first
    let cancelled = false;
    listResources()
      .then((all) => {
        if (cancelled) return;
        const here = all.filter((r) => r.locationId === loc.id);
        const match =
          here.find((r) => r.name.toLowerCase() === wanted)
          ?? here.find((r) => r.name.toLowerCase().includes(wanted));
        if (match) {
          fitterPrefilled.current = true;
          dispatch({ type: 'SELECT_FITTER', payload: match.id });
        }
      })
      .catch(() => {
        // Fitter list unavailable — leave the default "Any Fitter".
      });
    return () => {
      cancelled = true;
    };
  }, [presetFitterName, state.selectedLocation, state.selectedFitterId]);

  // Current studio/fitter mirrored into refs. The engage handler's closure is
  // stale (its effect deps deliberately exclude state), so it reads these to
  // tell an already-applied location-context from a fresh one. Without it,
  // reopening the modal re-dispatches SELECT_LOCATION — which clears the fitter
  // it then re-sets — and the selection flickers.
  const selectedLocationIdRef = useRef<string | null>(null);
  const selectedFitterIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedLocationIdRef.current = state.selectedLocation?.id ?? null;
  }, [state.selectedLocation]);
  useEffect(() => {
    selectedFitterIdRef.current = state.selectedFitterId;
  }, [state.selectedFitterId]);

  // Location-context fast-forward. Auto-complete Step 1 onto Step 2 ONLY when
  // everything Step 2 needs is already in hand: the studio, the visitor (name +
  // email, from the identity prefill), AND a fitting type. A fitter CTA carries
  // a studio and a fitter but NO fitting type, so it must not fast-forward —
  // that dropped the visitor on Step 2 with a fitter and nothing to book. When a
  // fitting type is missing the seed card stays up to collect it (and shows the
  // fitter alongside), then a normal Continue advances. Fires the same Step 1→2
  // analytics + lead writes as a manual Continue. Decided once per open.
  useEffect(() => {
    if (!ctxSeedCard || mode !== 'booking' || leadCaptureDone) return;
    if (!state.selectedLocation) return; // wait for the studio to resolve
    if (fastForwardDecided.current) return;
    fastForwardDecided.current = true;
    const nameOk = state.fullName.trim().length > 1;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim());
    if (nameOk && emailOk && state.selectedFittingType) {
      completeLeadCaptureRef.current();
    }
  }, [
    ctxSeedCard,
    mode,
    leadCaptureDone,
    state.selectedLocation,
    state.fullName,
    state.email,
    state.selectedFittingType,
  ]);

  const presetAppliedForLocation = useRef<string | null>(null);
  const presetOverridden = useRef(false);
  useEffect(() => {
    if (!presetFittingName) return;
    if (state.selectedFittingType
      && state.selectedFittingType.name.toLowerCase() !== presetFittingName.trim().toLowerCase()) {
      presetOverridden.current = true;
    }
  }, [presetFittingName, state.selectedFittingType]);
  useEffect(() => {
    if (!presetFittingName || presetOverridden.current || state.selectedFittingType) return;
    const locKey = state.selectedLocation?.id ?? '(none)';
    if (presetAppliedForLocation.current === locKey) return;
    const wanted = presetFittingName.trim().toLowerCase();
    const match = visibleFittingTypes(state.country, state.selectedLocation)
      .find((ft) => ft.name.toLowerCase() === wanted);
    if (match) {
      presetAppliedForLocation.current = locKey;
      dispatch({ type: 'SELECT_FITTING_TYPE', payload: match });
    }
  }, [presetFittingName, state.country, state.selectedLocation, state.selectedFittingType]);

  // Web_Promo deep link — if the current URL carries a ?web_promo=<CODE>
  // param, silently pre-validate it against the first location + a Driver
  // fitting so the promo field arrives pre-populated + marked as applied.
  // Invalid/unknown codes fail silently so the flow degrades gracefully.
  // Originally added by Sean for the referral-share URL pattern; preserved
  // through the monorepo sync 2026-08-12.
  const webPromoTriedRef = useRef(false);
  useEffect(() => {
    if (webPromoTriedRef.current) return;
    webPromoTriedRef.current = true;
    const code = getWebPromoCode();
    if (!code || isReadOnly()) return;
    let cancelled = false;
    (async () => {
      try {
        const locations = await listLocations();
        const loc = locations[0];
        if (!loc) return;
        const ctx = FITTING_TYPES.find((f) => f.id === 'driver') ?? FITTING_TYPES[0];
        if (!ctx) return;
        const start = new Date(Date.now() + 7 * 86400_000);
        start.setUTCMinutes(0, 0, 0);
        const res = await validatePromo({
          promoCode: code,
          locationId: loc.id,
          fittingTypeName: ctx.name,
          appointmentStartDateTime: start.toISOString(),
          price: parsePrice(pricingLabelFor(ctx, null)),
        });
        if (cancelled || !res.valid) return;
        dispatch({ type: 'SET_PROMO_CODE', payload: code });
        dispatch({
          type: 'VALIDATE_PROMO_SUCCESS',
          payload: {
            code,
            valid: true,
            discount: res.discount,
            message: res.message,
            terms: res.terms,
          },
        });
      } catch {
        // Silent by design — the deep link degrades to the normal flow.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Claim the single page-level dev TrafficDrawer for this instance if no other
  // instance already has (see trafficDrawerOwner above). Idempotent claim.
  const instanceIdRef = useRef<object>({});
  if (trafficDrawerOwner === null) {
    trafficDrawerOwner = instanceIdRef.current;
  }
  const ownsTrafficDrawer = trafficDrawerOwner === instanceIdRef.current;

  // Track the rendered root element in state so useContainerWidth can react
  // to it mounting (the div lives inside the modal portal and doesn't exist
  // until the modal opens). The ref is preserved for synchronous reads in
  // scroll handlers.
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Inline flow: the in-flow Step-1 card element + its captured rect at the
  // moment of expansion, so Step 2's modal can FLIP-grow out of exactly where
  // the card was (rather than the default slide-up).
  const inlineCardRef = useRef<HTMLDivElement | null>(null);
  const growRectRef = useRef<{ top: number; left: number; width: number; height: number } | null>(null);
  // Capture marketing-attribution once at mount. The embedding page's
  // URL / referrer / utm params are stable for the session, so a single
  // snapshot at first render is correct; reading them later (after the
  // user has navigated within the SPA host page) would drop attribution.
  // Set once a studio's own promotion code has been applied. The market-wide
  // code read at launch resolves asynchronously, so without this a slow
  // metadata response could land after a studio was chosen and replace that
  // studio's code with the less specific market default.
  const storePromoAppliedRef = useRef(false);
  // An explicit data-cc-promoverride from the clicked trigger. Held here so
  // the store-selection effect and the pricing resolver keep it: a campaign
  // page that pinned a code must keep it past the studio pick (Dan's AGL10
  // page, 2026-10-12, lost it the moment a store was chosen).
  const promoOverrideRef = useRef<string | null>(null);
  // The same override as state, so effects can depend on it: the studio is
  // often already selected (saved identity, store page) BEFORE the trigger
  // is clicked, and an effect keyed on the studio alone never sees the code
  // (Jon, 2026-10-13: MARCHFREE and a QA code applied nothing on a page
  // whose studio was prefilled).
  const [pinnedOverride, setPinnedOverride] = useState<string | null>(null);
  // Set when a booking attempt dies at the gateway. A later double-booking
  // conflict then reads as confirmation rather than a fresh contradiction.
  const bookingTimedOutRef = useRef(false);
  const marketingRef = useRef<MarketingPayload | null>(null);
  if (marketingRef.current === null && typeof window !== 'undefined') {
    marketingRef.current = captureMarketing();
  }
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const setRoot = useCallback((el: HTMLDivElement | null) => {
    rootRef.current = el;
    setRootEl(el);
  }, []);
  // Container-width detection was the right call in the inline-embed era
  // (the widget could land inside a narrow host-page column even on a
  // desktop monitor). In the modal era the widget controls its own
  // width — it's always 720px or full viewport — so the container can
  // drop below the desktop breakpoint by design (lead capture / strip
  // pre-location) without meaning we should switch to mobile rendering.
  // Drive isDesktop from the viewport instead so the strip's pre-location
  // step doesn't render the mobile field stack.
  const containerWidth = useContainerWidth(rootEl);
  void containerWidth; // legacy, retained for future per-embed re-introduction
  const isDesktop =
    (typeof window !== 'undefined' ? window.innerWidth : 1280) >= DESKTOP_BREAKPOINT;

  // Drive measured-and-clamped pixel dimensions on the modal shell so step
  // transitions animate via CSS. The browser can't interpolate between
  // two auto-computed dimensions, so we feed explicit px values and let
  // CSS transition between them.
  //
  // Height: measure the booking content with ResizeObserver, clamp to
  // 60vh-95vh so the modal never shrinks below or exceeds sensible bounds.
  //
  // Width: chosen per booking step so short single-column content
  // (lead capture, confirm, congrats) feels purposeful in a narrower
  // shell, while the booking strip + panel grid gets the room it needs
  // for the desktop 4-column layout to read correctly.
  const [contentScrollHeight, setContentScrollHeight] = useState<number | null>(null);
  const [viewportSize, setViewportSize] = useState<{ w: number; h: number }>(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 900,
  }));
  useEffect(() => {
    if (!rootEl) {
      setContentScrollHeight(null);
      return;
    }
    const measure = () => {
      setContentScrollHeight(rootEl.scrollHeight);
      setViewportSize({ w: window.innerWidth, h: window.innerHeight });
    };
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(rootEl);
    window.addEventListener('resize', measure);
    return () => {
      obs.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [rootEl]);

  // Pre-panel states (lead capture, just-strip pre-location, confirm,
  // congrats) all share a single consistent height so step transitions
  // animate width only — the modal stays the same vertical size across
  // those steps. Only the full-panel state (booking strip + lower panel
  // visible) is allowed to grow to fit the 4-column grid.
  const fullPanelVisible =
    leadCaptureDone && state.selectedLocation != null;

  // LeadCapture now hosts a 3-column row (Name · Email · Nearest Studio)
  // and needs more horizontal room than the prior 2-field layout. Widen the
  // modal to ~1180 just for that step; confirm/congrats stay at 720.
  const isLeadCaptureStep = mode === 'booking' && !leadCaptureDone;
  const targetWidth = (() => {
    const cap = viewportSize.w - 40;
    if (fullPanelVisible) return cap;
    if (isLeadCaptureStep) {
      // The store/fitter-context seed card stacks its fields in a single
      // column (same layout as the hero card), so it wants the narrow ~500px
      // width — NOT the wide 3-column lead-capture layout that earns 1180.
      if (ctxSeedCard) return Math.min(500, cap);
      return Math.min(1180, cap);
    }
    return Math.min(720, cap);
  })();

  const targetHeight = (() => {
    // Desktop reserves only ~6px total so the modal can sit close to the
    // viewport top/bottom edges (matches the desktop .modalContent max-height).
    const maxCap = viewportSize.h - (isDesktop ? 6 : 40);
    if (!fullPanelVisible) {
      // Fixed 70vh for every pre-panel state so they all match.
      return Math.round(viewportSize.h * 0.8);
    }
    const floor = Math.round(viewportSize.h * 0.6);
    const measured = contentScrollHeight ?? floor;
    // The ~35px "taller modal" on desktop is done by padding the modal's root
    // (see .modalContentInner > .root in the desktop media query), which grows
    // the measured content — NOT by adding to `measured` here, which would feed
    // back through the ResizeObserver and inflate the modal to the cap.
    return Math.max(floor, Math.min(measured, maxCap));
  })();

  const modalContentStyle = {
    height: `${targetHeight}px`,
    width: `${targetWidth}px`,
  };

  // Step indicator (1..3), cumulative fill. The three logical stages map:
  //   1 — Contact info        (lead capture)
  //   2 — Choose your fitting (booking strip)
  //   3 — Confirm appointment (confirm review)
  // The congrats screen sits outside the funnel — the user has already
  // finished, so the indicator hides entirely there (showProgress=false).
  const currentStep: 1 | 2 | 3 = (() => {
    if (mode === 'confirm-appointment') return 3;
    if (mode === 'booking' && leadCaptureDone) return 2;
    return 1;
  })();
  const showProgress = mode !== 'congrats';

  // Inline mounts never engage, so they take their market and promo from the
  // container at mount — the same precedence the engage handler below applies
  // per open for the modal (promoverride > country.initialPromoCode > the
  // market's SF master code > legacy default). Before this an inline mount left
  // state.country null, so an AU/UK/CA inline listed US studios.
  useEffect(() => {
    if (!inline) return;
    // state.country is already the mount market (see the useReducer init).
    promoOverrideRef.current = mountPromoOverride ?? null;
    setPinnedOverride(mountPromoOverride ?? null);
    const masterAtMount = mountPromoOverride ?? mountCountry?.initialPromoCode ?? null;
    if (masterAtMount) {
      dispatch({ type: 'SET_MASTER_PROMO', payload: { code: masterAtMount, terms: null } });
    }
    if (!mountPromoOverride) {
      const marketCode = mountCountry?.code ?? 'us';
      void getMasterPromoCodes().then((codes) => {
        if (storePromoAppliedRef.current) return;
        const code = codes[marketCode];
        if (code) {
          dispatch({ type: 'SET_MASTER_PROMO', payload: { code, terms: null } });
        }
      });
    }
    // Mount-time only: the container's attributes do not change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Engagement listener — host-page BOOK NOW dispatches `cc:booking-widget:engage`.
  //
  // ONLY the modal instance reacts. Inline instances are always-on (they render
  // Step 1 immediately) and must NOT respond: otherwise clicking any modal
  // button would flip every inline instance to `booking` and double-fire
  // book_flow_start. Gate on !inline.
  useEffect(() => {
    if (inline) return;
    const onEngage = (e: Event) => {
      // Per-open flavour + attribution ride on the event detail — main.tsx
      // reads them off the specific trigger that was clicked. Fall back to this
      // instance's props when a bare engage is dispatched.
      const detail = (e as CustomEvent).detail as
        | {
            source?: string;
            phoneMandatory?: boolean;
            embedSource?: string;
            country?: CountryConfig | null;
            promoOverride?: string | null;
            presetStoreName?: string | null;
            presetFitterName?: string | null;
          }
        | undefined;
      const src = detail?.embedSource ?? embedSource;
      setPhoneMandatoryState(detail?.phoneMandatory ?? phoneMandatory);
      setMode('booking');
      // Country scope — threaded to state so downstream calls (like
      // listLocations) can query the middleware for country-scoped data.
      dispatch({ type: 'SET_COUNTRY', payload: detail?.country?.code ?? null });

      // Master-promo precedence at engage time:
      //   1. data-cc-promoverride — absolute override (campaign pages, tests)
      //   2. country.initialPromoCode — country-default starter promo
      //   3. no auto-apply — leave master at its FITTINGUS legacy default
      // Applied silently as the master; NEVER written to state.promoCode
      // (which is reserved for a user-entered secondary code and stays empty).
      // The SELECT_LOCATION effect below overwrites this with the SF-active
      // code once we know which store the user picked.
      promoOverrideRef.current = detail?.promoOverride ?? null;
      setPinnedOverride(detail?.promoOverride ?? null);
      const masterAtEngage = detail?.promoOverride ?? detail?.country?.initialPromoCode ?? null;
      if (masterAtEngage) {
        dispatch({ type: 'SET_MASTER_PROMO', payload: { code: masterAtEngage, terms: null } });
      }

      // Salesforce is the authority on which promotion each market is running,
      // and the answer differs between orgs — the QA org runs a different US
      // code to production — so the country-config value above is a starting
      // guess, not a fact. Read the real one at launch, as the client asked.
      //
      // An explicit data-cc-promoverride is never second-guessed: a campaign
      // page that pinned a code must keep it. And this only fills the gap
      // before a studio is chosen — SELECT_LOCATION still prefers that store's
      // own active code, which is more specific than a market-wide default.
      if (!detail?.promoOverride) {
        const marketCode = detail?.country?.code ?? 'us';
        void getMasterPromoCodes().then((codes) => {
          if (storePromoAppliedRef.current) return;
          const code = codes[marketCode];
          if (code) {
            dispatch({ type: 'SET_MASTER_PROMO', payload: { code, terms: null } });
          }
        });
      }
      // Location-context modal: a fitter's "Book fitting" CTA opens the shared
      // modal carrying its studio (and itself) so the booking is preselected FOR
      // that fitter at that studio — overriding the favourite-store hint, as on
      // the store detail page. Resolved directly here (not via the inline preset
      // effects, which serve the always-on inline card) and the prefill guards
      // are stood down so the favourite effect doesn't re-impose a store after.
      const forcedStore = detail?.presetStoreName?.trim();
      const forcedFitter = detail?.presetFitterName?.trim();
      // A location-context open (a store/fitter CTA) presents the seed card and
      // fast-forwards; an ordinary BOOK NOW is the plain modal. Decide per open,
      // and re-arm the once-per-open fast-forward decision.
      setCtxSeedCard(!!forcedStore);
      fastForwardDecided.current = false;
      if (forcedStore) {
        storePrefilled.current = true;
        void listLocations(detail?.country?.code ?? null)
          .then((locs) => {
            const loc = matchStoredStore(locs, { storeName: forcedStore });
            if (!loc) return;
            // Only (re)select the studio when it isn't already current.
            // SELECT_LOCATION clears the whole downstream cascade (fitter,
            // fitting type, date, slot) AND lands a fresh Location object that
            // re-triggers store-scoped effects — so re-dispatching it on every
            // reopen wipes and re-sets the fitter, which flickers. storeChanged
            // also tells the fitter step whether the cascade just cleared it.
            const storeChanged = selectedLocationIdRef.current !== loc.id;
            if (storeChanged) {
              dispatch({ type: 'SELECT_LOCATION', payload: loc });
            }
            if (!forcedFitter) return;
            const want = forcedFitter.toLowerCase();
            return listResources().then((all) => {
              const here = all.filter((r) => r.locationId === loc.id);
              const f =
                here.find((r) => r.name.toLowerCase() === want)
                ?? here.find((r) => r.name.toLowerCase().includes(want));
              if (!f) return;
              // Set the fitter when the studio just changed (the cascade cleared
              // it) or when a different one is selected. Skip the pure no-op —
              // same studio, same fitter — which was the reopen churn.
              if (storeChanged || selectedFitterIdRef.current !== f.id) {
                fitterPrefilled.current = true;
                dispatch({ type: 'SELECT_FITTER', payload: f.id });
              }
            });
          })
          .catch(() => {
            // Resolution failed — the modal still opens; the studio/fitter just
            // start unset and the customer picks as usual.
          });
      }

      emit('cc:booking-widget:engaged', { source: src });
      emitAnalytics({
        event: 'book_flow_start',
        embed_source: src,
        entry_source: window.location.pathname || 'unknown',
      });
      // Scroll the widget mount point into view (offset for sticky-ish host header).
      const mount = document.querySelector('[data-cc-booking-widget]');
      if (mount) {
        const top = (mount as HTMLElement).getBoundingClientRect().top + window.scrollY - 24;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    };
    window.addEventListener(ENGAGE_EVENT, onEngage);
    return () => window.removeEventListener(ENGAGE_EVENT, onEngage);
  }, [inline, embedSource, phoneMandatory]);

  // State-change watchers — fire analytics events when the user advances values.
  // Refs track previous values so we only emit on transitions, not on every render.
  const prevLocationId = useRef<string | null>(null);
  const prevDate = useRef<string | null>(null);
  const prevSlotKey = useRef<string | null>(null);
  const confirmScreenFired = useRef<boolean>(false);
  const confirmEnteredFromLocation = useRef<string | null>(null);
  const confirmEnteredFromFittingId = useRef<string | null>(null);
  const prevFittingTypeId = useRef<string | null>(null);
  const fittingTypeSelectedFired = useRef<boolean>(false);

  // On store select, resolve the store-specific fitting-name map. SF's
  // Product2 records are country-suffixed for international stores, so
  // sending "Wedge" to a UK store returns 400. This effect fires against
  // the middleware's /fittings endpoint on every SELECT_LOCATION, then
  // stashes the canonical→wire map in state. Failures leave the map null,
  // which means downstream calls send canonical names (correct for US,
  // wrong for international — treat that as an operational alert to
  // investigate rather than a silent regression).
  useEffect(() => {
    const store = state.selectedLocation;
    if (!store) return;
    let cancelled = false;
    listStoreFittings(store.name)
      .then((resp) => {
        if (cancelled) return;
        dispatch({
          type: 'SET_FITTING_NAME_MAP',
          payload: buildFittingNameMap(resp.fittings),
        });
        dispatch({ type: 'SET_FITTING_LIST_PRICES', payload: buildFittingPriceMap(resp.fittings) });
        // Master promo: prefer the SF-active code + its terms text over
        // the country-config default set at engage. Applied silently — no
        // effect on the user's input field. The user's optional secondary
        // code (state.promoCode) is left untouched.
        // Unless the page pinned a code: an explicit data-cc-promoverride
        // survives the studio pick, as its contract promises.
        if (resp.activePromoCode && !promoOverrideRef.current) {
          storePromoAppliedRef.current = true;
          dispatch({
            type: 'SET_MASTER_PROMO',
            payload: { code: resp.activePromoCode, terms: resp.activePromoTerms },
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn(
          '[cc] listStoreFittings failed for',
          store.name,
          err instanceof Error ? err.message : err,
        );
        dispatch({ type: 'SET_FITTING_NAME_MAP', payload: null });
      });
    return () => {
      cancelled = true;
    };
  }, [state.selectedLocation]);

  // A pinned code (data-cc-promoverride / ?cc_promoverride=) is validated
  // against the chosen studio and SHOWN as applied, the way the Web_Promo
  // deep link shows a referral code. Keyed on both the override and the
  // studio so it runs whichever arrives second. A code the studio rejects
  // is logged, not shown, so a tester can see why.
  //
  // Validated against the fitting on screen once one is chosen — its market
  // price and the chosen slot — and re-run as those change, so the "fitting
  // fee now $0 (was $X)" line describes the selected fitting rather than
  // whichever fitting the studio lists first (Dan, 2026-10-13: a Full Bag
  // showed "was $250", the first fitting's price). Before a fitting is
  // chosen the first priced fitting stands in, only to establish validity.
  const pinnedFitting = state.selectedFittingType;
  const pinnedSlotStart = state.selectedSlot?.start;
  useEffect(() => {
    const code = pinnedOverride;
    const loc = state.selectedLocation;
    if (!code || !loc) return;
    let cancelled = false;
    (async () => {
      try {
        let fittingTypeName: string;
        let price: number;
        if (pinnedFitting) {
          fittingTypeName = resolveWireName(pinnedFitting.name, state.fittingNameMap);
          price = listPriceFor(
            fittingTypeName,
            state.fittingListPrices,
            parsePrice(pricingLabelFor(pinnedFitting, state.country)),
          );
        } else {
          const resp = await listStoreFittings(loc.name);
          const first = (resp.fittings ?? []).find(
            (f) => (f.schedulingAppName ?? '') !== '' && Number(f.unitPrice ?? 0) > 0,
          );
          if (cancelled || !first) return;
          fittingTypeName = first.schedulingAppName ?? '';
          price = Number(first.unitPrice ?? 0);
        }
        const start = new Date(Date.now() + 7 * 86400_000);
        start.setUTCMinutes(0, 0, 0);
        const res = await validatePromo({
          promoCode: code,
          locationId: loc.id,
          fittingTypeName,
          appointmentStartDateTime: pinnedSlotStart ?? start.toISOString(),
          price,
          email: state.email.trim() || undefined,
        });
        if (cancelled) return;
        if (!res.valid) {
          // eslint-disable-next-line no-console
          console.warn(`[CC widget] promoOverride=${code} rejected for ${loc.name}: ${res.message ?? 'invalid'}`);
          return;
        }
        // eslint-disable-next-line no-console
        console.info(`[CC widget] promoOverride=${code} applied for ${loc.name}`);
        dispatch({ type: 'SET_PROMO_CODE', payload: code });
        dispatch({
          type: 'VALIDATE_PROMO_SUCCESS',
          payload: { code, valid: true, discount: res.discount, message: res.message, terms: res.terms },
        });
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[CC widget] promoOverride validation failed', err instanceof Error ? err.message : err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pinnedOverride, state.selectedLocation, pinnedFitting, pinnedSlotStart]);

  // Promotional pricing, resolved from Salesforce rather than authored.
  //
  // Three triggers, because the answer changes on each:
  //   1. store chosen        — the master promotion is store-scoped
  //   2. user code validated — their code may beat the master
  //   3. email becomes valid — Salesforce decides referral and PGA
  //                            eligibility from it
  //
  // (3) was removed on 2026-08-20 after measuring that POLI113429 answered
  // identically with and without an email, and it cost a full re-resolution on
  // every keystroke. Re-measured 2026-09-01, the premise no longer holds: the
  // same referral code is valid with an email and rejected without it, and a
  // PGA code is the reverse. The keystroke cost is avoided by depending on the
  // email only once it is well-formed, rather than on every character — one
  // extra resolution per booking, not one per keypress.
  //
  // The grid renders before a slot is picked, so promotions are evaluated
  // against a synthetic near-future time — the same trick the Web_Promo
  // deep-link path uses. Once a real slot exists we use it, so the price
  // shown is evaluated against the appointment actually being booked.
  const store = state.selectedLocation;
  const userCode = state.promoValidation?.valid ? state.promoValidatedCode : null;
  // Only a well-formed address, so a half-typed one neither re-resolves the
  // grid nor spends calls against /promos/validate's rate limit.
  const pricingEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim())
    ? state.email.trim()
    : undefined;
  useEffect(() => {
    if (!store) return;
    let cancelled = false;

    (async () => {
      try {
        const resp = await listStoreFittings(store.name);
        const fittings = (resp.fittings ?? [])
          .map((f) => ({ name: f.schedulingAppName ?? '', unitPrice: Number(f.unitPrice ?? 0) }))
          .filter((f) => f.name !== '' && f.unitPrice > 0);
        if (cancelled || !fittings.length) return;

        const slotStart =
          state.selectedSlot?.start ??
          new Date(Date.now() + 7 * 86400_000).toISOString();

        const pricing = await resolvePromoPricing({
          locationId: store.id,
          fittings,
          // The pinned override prices the session, not the store's code.
          codes: [pinnedOverride ?? resp.activePromoCode ?? state.masterPromoCode, userCode],
          slotStart,
          email: pricingEmail,
        });
        if (cancelled) return;

        dispatch({ type: 'SET_PROMO_PRICING', payload: pricing });

        // Terms are deliberately NOT dispatched from here. Validation
        // already puts the right text in state, and SET_MASTER_PROMO now
        // refuses to overwrite a visitor's applied terms — driving terms
        // from the pricing winner would have reintroduced exactly the bug
        // the reducer guard fixes, because a referral carries no computed
        // discount and so can never be the winner.
      } catch {
        // Pricing must never break the grid — fall back to list prices.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [store, userCode, state.selectedSlot?.start, pricingEmail, pinnedOverride]);

  // Booking details → the Marketing Cloud funnel row. Once step 1 has put
  // the customer on a row, every change to what they are booking (fitting,
  // fitter, day, time, promo code) is reported so an unfinished booking still
  // says what it was for. See analytics/booking-details.ts.
  useBookingDetailsReport(
    state,
    marketingRef.current?.submissionId,
    // The selection screen and the review screen: the promo code is typed on
    // the review screen, and the customer can still go back and change the rest.
    leadCaptureDone && (mode === 'booking' || mode === 'confirm-appointment'),
  );

  // Location selected — fires book_flow_loc_selected. If a previous
  // location was set and the new one is different, also fires loc_change.
  useEffect(() => {
    if (state.selectedLocation && state.selectedLocation.id !== prevLocationId.current) {
      const previousId = prevLocationId.current;
      prevLocationId.current = state.selectedLocation.id;
      // Reschedule hydration sets these values programmatically — emitting the
      // booking-funnel selection events for them would fabricate funnel steps
      // the user never took. Refs still update (above) so exiting the mode
      // doesn't fire a spurious "change" against the hydrated value.
      if (state.rescheduleMode) return;
      emitAnalytics({
        event: 'book_flow_loc_selected',
        location: state.selectedLocation.name,
        location_id: state.selectedLocation.id,
      });
      if (previousId !== null) {
        emitAnalytics({
          event: 'book_flow_loc_change',
          location_from: previousId,
          location_to: state.selectedLocation.id,
        });
      }
    }
  }, [state.selectedLocation]);

  // Date selected — fires book_flow_date_selected on the first pick, then
  // book_flow_date_change on every later change (prevDate.current === null
  // marks "no prior selection yet").
  useEffect(() => {
    if (state.selectedDate && state.selectedDate !== prevDate.current) {
      const isFirst = prevDate.current === null;
      prevDate.current = state.selectedDate;
      // See the reschedule note on the location watcher above.
      if (state.rescheduleMode) return;
      emitAnalytics({
        event: isFirst ? 'book_flow_date_selected' : 'book_flow_date_change',
        date_selected: state.selectedDate,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    }
  }, [state.selectedDate]);

  // Time slot selected — fires book_flow_time_selected on the first pick,
  // then book_flow_time_change on every later change. Carries cumulative
  // location + fitting_selection so sGTM can reconstruct legacy `booking step=3`
  // with the full parameter shape the existing funnel expects.
  useEffect(() => {
    if (state.selectedSlot && state.selectedLocation && state.selectedFittingType) {
      const key = `${state.selectedSlot.start}|${state.selectedSlot.fitterId}`;
      if (key !== prevSlotKey.current) {
        const isFirst = prevSlotKey.current === null;
        prevSlotKey.current = key;
        // See the reschedule note on the location watcher above.
        if (state.rescheduleMode) return;
        const startDate = new Date(state.selectedSlot.start);
        const hh = String(startDate.getHours()).padStart(2, '0');
        const mm = String(startDate.getMinutes()).padStart(2, '0');
        emitAnalytics({
          event: isFirst ? 'book_flow_time_selected' : 'book_flow_time_change',
          time_selected: `${hh}:${mm}`,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          location: state.selectedLocation.name,
          fitting_selection: state.selectedFittingType.name,
        });
      }
    }
  }, [state.selectedSlot, state.selectedLocation, state.selectedFittingType]);

  // Composite event: book_flow_fitting_type_selected. Fires once per
  // session when location and fitting type are set
  // (ONCE_PER_SESSION dedup is enforced inside the dispatch module); every
  // later change to the fitting type alone fires book_flow_fitting_type_change
  // instead.
  useEffect(() => {
    if (state.selectedLocation && state.selectedFittingType) {
      const isFirst = !fittingTypeSelectedFired.current;
      const changed = state.selectedFittingType.id !== prevFittingTypeId.current;
      if (isFirst || changed) {
        fittingTypeSelectedFired.current = true;
        prevFittingTypeId.current = state.selectedFittingType.id;
        // See the reschedule note on the location watcher above.
        if (state.rescheduleMode) return;
        emitAnalytics({
          event: isFirst ? 'book_flow_fitting_type_selected' : 'book_flow_fitting_type_change',
          location: state.selectedLocation.name,
          fitting_selection: state.selectedFittingType.name,
          // date_selected: state.selectedDate,
        });
      }
    }
  }, [state.selectedLocation, state.selectedFittingType, state.selectedDate]);

  // Confirm appointment screen entry — fires book_flow_summary once.
  // Not in reschedule mode: the summary is a booking-funnel step, and the
  // reschedule flow deliberately stays out of the funnel.
  useEffect(() => {
    if (state.rescheduleMode) return;
    if (mode === 'confirm-appointment' && !confirmScreenFired.current) {
      if (
        !state.selectedLocation ||
        !state.selectedFittingType ||
        !state.selectedDate ||
        !state.selectedSlot
      ) {
        return;
      }
      confirmScreenFired.current = true;
      const slotStart = new Date(state.selectedSlot.start);
      const hh = String(slotStart.getHours()).padStart(2, '0');
      const mm = String(slotStart.getMinutes()).padStart(2, '0');
      const time_selected = `${hh}:${mm}`;
      emitAnalytics({
        event: 'book_flow_summary',
        location: state.selectedLocation.name,
        fitting_selection: state.selectedFittingType.name,
        date_selected: state.selectedDate,
        time_selected,
        promo_code: state.promoCode.trim() || undefined,
        changed_location:
          confirmEnteredFromLocation.current !== null &&
          confirmEnteredFromLocation.current !== state.selectedLocation.id,
        changed_fitting_type:
          confirmEnteredFromFittingId.current !== null &&
          confirmEnteredFromFittingId.current !== state.selectedFittingType.id,
        value: parsePrice(pricingLabelFor(state.selectedFittingType, state.country)),
        currency: countryConfigOrDefault(state.country).currency,
      });
    }
  }, [
    mode,
    state.selectedLocation,
    state.selectedFittingType,
    state.selectedDate,
    state.selectedSlot,
    state.promoCode,
  ]);

  // Global click handler — fires book_flow_click for any element with a
  // data-cc-track attribute. Donald's GTM triggers can also bind to these
  // attributes directly; this gives us GA4 visibility regardless. Attached
  // page-globally exactly once (see ensureGlobalClickTracking) so it fires
  // once per click no matter how many Widget instances are mounted.
  useEffect(() => {
    ensureGlobalClickTracking();
  }, []);

  // ── Reschedule flow ──────────────────────────────────────────────────────
  //
  // Session caches for hydration lookups. Locations are fetched with the
  // widest scope ('all') because the appointment on file may live outside the
  // embed's country filter; resources back the fitter name→id match. Both are
  // fetch-once-per-session, same as the client's master-promo cache.
  const allLocationsRef = useRef<Location[] | null>(null);
  const allResourcesRef = useRef<Resource[] | null>(null);

  const getAllLocations = async (): Promise<Location[]> => {
    if (allLocationsRef.current) return allLocationsRef.current;
    const locations = await listLocations('all');
    allLocationsRef.current = locations;
    return locations;
  };

  const getAllResources = async (): Promise<Resource[]> => {
    if (allResourcesRef.current) return allResourcesRef.current;
    const resources = await listResources();
    allResourcesRef.current = resources;
    return resources;
  };

  // FPO copy — the middleware answers 404 identically for "no appointment"
  // and "identity mismatch", so this is the one message for both.
  const RESCHEDULE_NOT_FOUND_MSG =
    "We couldn't find an upcoming appointment for that name and email.";

  /**
   * Populate the booking flow from a looked-up appointment, then land on the
   * confirm screen (full hydration) or the booking strip (partial — the
   * fitting or slot couldn't be mapped, so the user re-picks from there).
   *
   * All slot times stay in fake-Z: `appt.start` already carries the store
   * wall-clock behind a fake Z, and the end is derived by string-safe UTC
   * arithmetic (addMinutesToFakeZ) — never local-timezone Date math.
   */
  const hydrateFromAppointment = async (appt: LookedUpAppointment): Promise<void> => {
    try {
      // 1. Location — exact name match first, then case-insensitive/trimmed.
      const locations = await getAllLocations();
      const wantedName = appt.storeName.trim();
      const location =
        locations.find((l) => l.name === appt.storeName) ??
        locations.find(
          (l) => l.name.trim().toLowerCase() === wantedName.toLowerCase(),
        ) ??
        null;
      if (!location) {
        // FPO copy. Without a Location record the widget can't drive
        // availability for the store, so self-service ends here.
        dispatch({
          type: 'RESCHEDULE_LOOKUP_ERROR',
          payload: {
            message:
              'We found your appointment, but online rescheduling isn’t available ' +
              'for that studio right now. Please call the studio and they’ll move it for you.',
          },
        });
        return;
      }

      // 2. Fitting type — strip the country suffix off the wire name and
      // match the widget's canonical catalog case-insensitively. Unmappable
      // (a product the widget doesn't sell, e.g. a retired promo fitting)
      // degrades to null → the user re-picks on the strip.
      const rawService = appt.serviceNames[0] ?? '';
      const canonical = stripCountrySuffix(rawService).toLowerCase().replace(/\s+/g, ' ').trim();
      const fittingType =
        canonical === ''
          ? null
          : FITTING_TYPES.find(
              (f) => f.name.toLowerCase().replace(/\s+/g, ' ').trim() === canonical,
            ) ?? null;

      // 3. Date + slot — only when the middleware parsed a structured start.
      // The slot needs the fitting's duration for its end, so no fitting type
      // also means no slot (date alone still hydrates).
      const date = appt.start ? appt.start.slice(0, 10) : null;
      let slot: SelectedSlot | null = null;
      if (appt.start && fittingType) {
        // Fitter: SF gives us a display name only (formula field, no ID) —
        // match it against the store's roster; anything else degrades to the
        // AnyFitter bucket, which the reschedule POST omits so SF auto-assigns.
        let fitterId = 'AnyFitter';
        const fitterDisplayName = appt.fitterName?.trim() || 'Any Fitter';
        if (appt.fitterName?.trim()) {
          try {
            const resources = await getAllResources();
            const wanted = appt.fitterName.trim().toLowerCase();
            const match = resources.find(
              (r) =>
                r.locationId === location.id && r.name.trim().toLowerCase() === wanted,
            );
            if (match) fitterId = match.id;
          } catch {
            // Roster unavailable — AnyFitter is the safe degradation.
          }
        }
        slot = {
          start: appt.start,
          end: addMinutesToFakeZ(appt.start, fittingType.durationMinutes),
          fitterId,
          fitterName: fitterDisplayName,
        };
      }

      // 4. Phone — SF's stored number, normalized through the same formatter
      // the Step-1 field uses so the confirm screen shows it consistently.
      const phone = formatPhoneFromInput(appt.phone ?? '', state.country);

      // 5. One atomic dispatch (see HYDRATE_FROM_APPOINTMENT in machine.ts),
      // then land the user. Fully-mapped appointments go straight to the
      // confirm screen; partial hydration lands on the booking strip so the
      // user fills the gaps with the normal pickers.
      dispatch({
        type: 'HYDRATE_FROM_APPOINTMENT',
        payload: { appointment: appt, location, fittingType, date, slot, phone },
      });
      setHoldExpired(false);
      setLeadCaptureDone(true);
      if (slot && fittingType) {
        confirmEnteredFromLocation.current = location.id;
        confirmEnteredFromFittingId.current = fittingType.id;
        dispatch({ type: 'CLEAR_SUBMIT_ERROR' });
        setMode('confirm-appointment');
      } else {
        setMode('booking');
      }
    } catch (err) {
      dispatch({
        type: 'RESCHEDULE_LOOKUP_ERROR',
        payload: {
          message:
            err instanceof ApiError && err.status === 404
              ? RESCHEDULE_NOT_FOUND_MSG
              : 'Something went wrong loading your appointment. Please try again.',
        },
      });
    }
  };

  /**
   * Step-1 submit in reschedule mode — the identity lookup. Replaces the
   * lead-post/analytics booking path entirely (no lead vault write, no
   * Account seed, no funnel events: the customer already exists).
   */
  const runRescheduleLookup = async (
    identity: { email: string; name: string },
    targetAppointment?: string,
  ): Promise<void> => {
    dispatch({ type: 'RESCHEDULE_LOOKUP_START' });
    try {
      let appointments = await lookupAppointments({
        email: identity.email.trim(),
        name: identity.name.trim(),
      });
      // A caller that already knows WHICH appointment (the manage-appointment
      // page's Reschedule button) narrows a multi-match to it, skipping the
      // picker. -WL suffixes are ignored on both sides of the comparison. An
      // unmatched target falls through to the normal picker rather than 404ing
      // an identity the lookup just verified.
      if (targetAppointment && appointments.length > 1) {
        const want = targetAppointment.replace(/-WL$/i, '').toUpperCase();
        const narrowed = appointments.filter(
          (a) => a.appointmentNumber.replace(/-WL$/i, '').toUpperCase() === want,
        );
        if (narrowed.length === 1) appointments = narrowed;
      }
      if (appointments.length === 0) {
        // Contract says a 200 is non-empty; treat an empty list like a 404
        // anyway rather than hanging the UI on a violated assumption.
        dispatch({
          type: 'RESCHEDULE_LOOKUP_ERROR',
          payload: { message: RESCHEDULE_NOT_FOUND_MSG },
        });
        return;
      }
      if (appointments.length === 1) {
        await hydrateFromAppointment(appointments[0]);
        return;
      }
      dispatch({
        type: 'RESCHEDULE_LOOKUP_SUCCESS',
        payload: { candidates: appointments },
      });
    } catch (err) {
      dispatch({
        type: 'RESCHEDULE_LOOKUP_ERROR',
        payload: {
          message:
            err instanceof ApiError && err.status === 404
              ? RESCHEDULE_NOT_FOUND_MSG
              : 'Something went wrong looking up your appointment. Please try again.',
        },
      });
    }
  };

  // Step-1 submit in reschedule mode — the identity comes from the form state.
  const handleRescheduleLookup = (): Promise<void> =>
    runRescheduleLookup({ email: state.email, name: state.fullName });

  // Boot straight into reschedule (data-cc-mode="reschedule" inline mounts):
  // enter the mode with the identity prefilled and run the lookup immediately,
  // so the widget lands on the confirm screen (or picker) without the user
  // retyping what the host page already collected.
  // A closed reschedule mount renders NOTHING. Without this, closing the
  // flow's modal fell back to inline Step 1, which for a rescheduleBoot
  // mount is the "Pulling up your appointment…" card — spinning forever,
  // since the boot guard never re-runs.
  const [rescheduleClosed, setRescheduleClosed] = useState(false);
  const rescheduleBootRan = useRef(false);
  useEffect(() => {
    if (!rescheduleBoot || rescheduleBootRan.current) return;
    rescheduleBootRan.current = true;
    dispatch({ type: 'ENTER_RESCHEDULE_MODE' });
    dispatch({
      type: 'SET_LEAD',
      payload: { fullName: rescheduleBoot.name, email: rescheduleBoot.email },
    });
    // Warm the locations cache in parallel — hydrateFromAppointment awaits it
    // right after the lookup lands, and fetching the two serially was most of
    // the boot's dead time.
    void getAllLocations().catch(() => {});
    void runRescheduleLookup(
      { email: rescheduleBoot.email, name: rescheduleBoot.name },
      rescheduleBoot.appointment,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rescheduleBoot]);

  /**
   * Confirm-screen submit in reschedule mode — POST /bookings/:id/reschedule.
   * Reuses SUBMIT_START/SUCCESS/ERROR so ConfirmAppointment's spinner and
   * error rendering are untouched. Deliberately skips the booking conversion
   * analytics (GA4 purchase, ChannelMix, Meta/SFMC side effects) — a moved
   * appointment is not a new conversion.
   */
  const handleConfirmReschedule = async () => {
    if (
      !state.originalAppointment ||
      !state.selectedLocation ||
      !state.selectedFittingType ||
      !state.selectedDate ||
      !state.selectedSlot
    )
      return;

    dispatch({ type: 'SUBMIT_START' });

    const stripWl = (n: string) => n.replace(/-WL$/i, '');
    const payload: ReschedulePayload = {
      email: state.email.trim(),
      storeName: state.selectedLocation.name,
      // Already fake-Z — forwarded as-is, no conversion.
      appointmentStartDateTime: state.selectedSlot.start,
      serviceList: [resolveWireName(state.selectedFittingType.name, state.fittingNameMap)],
      fitterId:
        state.selectedSlot.fitterId !== 'AnyFitter' ? state.selectedSlot.fitterId : undefined,
      phone: state.phone.trim() || undefined,
      // Only a freshly user-validated code rides along — never the silent
      // master, and never the original booking's code (per plan: default is
      // no re-send, pending CC sign-off).
      promocode:
        state.promoValidatedCode && state.promoValidation?.valid
          ? state.promoValidatedCode
          : undefined,
    };

    try {
      const booking = await rescheduleBooking(
        stripWl(state.originalAppointment.appointmentNumber),
        payload,
      );
      dispatch({ type: 'SUBMIT_SUCCESS', payload: booking });

      const timeMatch = /T(\d{2}):(\d{2})/.exec(state.selectedSlot.start);
      emitAnalytics({
        event: 'book_flow_reschedule_confirmed',
        a_number: booking.id,
        location: state.selectedLocation.name,
        fitting_selection: state.selectedFittingType.name,
        date_selected: state.selectedDate,
        time_selected: timeMatch ? `${timeMatch[1]}:${timeMatch[2]}` : undefined,
      });

      setMode('congrats');
    } catch (err) {
      // Same triage order as the booking path: a gateway timeout is "we don't
      // know", a structured middleware message is worth showing, and the
      // fallback names the likeliest fix instead of echoing a status code.
      const msg =
        gatewayTimeoutMessage(err) ??
        apiErrorMessage(err) ??
        (err instanceof ApiError
          ? err.status === 404
            ? 'We couldn’t find that appointment anymore — it may have been ' +
              'canceled or already moved. Please call the studio and they’ll sort it out.'
            : 'We couldn’t reschedule this appointment. Please try again, or call ' +
              'the studio and they will sort it out.'
          : err instanceof Error
            ? err.message
            : 'Reschedule failed for an unknown reason.');
      dispatch({ type: 'SUBMIT_ERROR', payload: msg });
      emitAnalytics({ event: 'booking_error', error_message: msg });
    }
  };

  // Post the booking to the middleware → SF Schedule. Called from the Confirm
  // Appointment screen's Confirm button.
  const handleConfirmBooking = async () => {
    if (
      !state.selectedLocation ||
      !state.selectedFittingType ||
      !state.selectedDate ||
      !state.selectedSlot
    )
      return;

    dispatch({ type: 'SUBMIT_START' });

    const { firstName, lastName } = splitName(state.fullName);
    const notesParts: string[] = [];
    notesParts.push(`Fitting type: ${state.selectedFittingType.name}`);
    if (state.promoOptIn) notesParts.push('Customer opted into marketing promos');

    const payload: BookingPayload = {
      resourceId: state.selectedSlot.fitterId,
      start: state.selectedSlot.start,
      end: state.selectedSlot.end,
      fittingType: resolveWireName(state.selectedFittingType.name, state.fittingNameMap),
      customer: {
        firstName,
        lastName,
        email: state.email.trim(),
        // Rides on the booking itself since 2026-10-10. Before, the phone only
        // reached Salesforce through the Step 1 account seed and the
        // post-booking update; the international markets now collect it on
        // the Confirm step, after the seed and before a form the customer may
        // never submit, so the booking is the one write guaranteed to carry
        // it. Undefined where there is none — never an empty string, which
        // would overwrite a number already on the account.
        phone: phoneForBookingApi(state.phone, state.country, state.dialCodeOverride),
      },
      notes: notesParts.join(' · '),
      promoCode: buildPromoCodeForSchedule(
        state.masterPromoCode,
        state.promoValidatedCode,
        state.promoValidation?.valid,
      ),
      // The survey answer rides on Schedule, which is the only place
      // Salesforce accepts it — see the note on the confirm step.
      // "How did you hear about us?" no longer travels here. It was folded
      // into this payload only because Salesforce accepted it on Schedule and
      // nowhere else; PostAppointmentUpdate took it on 2026-08-27, so it is
      // asked and sent on the confirmation screen instead. See GetSetUp.tsx.
      marketing: marketingRef.current ?? undefined,
      // Gates the Marketing Cloud funnel report — US only for now. Must match
      // the value sent with the Step 1 seed, or the completion lands outside
      // the gate and the session looks abandoned. state.country is null on the
      // US embed — data-cc-countrycode is only set on the international host
      // pages — so null must map to 'us' or no US traffic is ever reported.
      market: state.country ?? 'us',
    };
    // Temporary debug — confirms the master + optional user code that
    // made it onto the Schedule payload. Helpful while validating the
    // silent-master model against staging bookings.
    // eslint-disable-next-line no-console
    console.log('[cc] /bookings promoCode =', payload.promoCode, {
      masterPromoCode: state.masterPromoCode,
      userPromoCode: state.promoCode,
      promoValidatedCode: state.promoValidatedCode,
      promoValidationValid: state.promoValidation?.valid,
    });

    try {
      const booking = await createBooking(payload);
      dispatch({ type: 'SUBMIT_SUCCESS', payload: booking });
      emit('cc:booking-widget:booking-confirmed', {
        confirmationNumber: booking.id,
        bookingId: booking.id,
        fittingType: state.selectedFittingType.name,
        location: state.selectedLocation.name,
      });

      // DISABLED 2026-06-18: setting user_id from booking.accountId here was
      // triggering GA4 identity stitching on completion events. The new
      // identity has no recorded source, so traffic_source resets to (not set)
      // → Channel = Unassigned. Symptom: book_flow_confirm_a_number +
      // channelmix_conversion were 100% / 87% Unassigned while every
      // event firing BEFORE this call attributed correctly. Re-enable
      // once we have an identity-attached strategy that doesn't override
      // session source (e.g., set on first emit, not at completion).
      // if (booking.accountId) setAnalyticsUserId(booking.accountId);

      const slotStart = new Date(state.selectedSlot.start);
      const hh = String(slotStart.getHours()).padStart(2, '0');
      const mm = String(slotStart.getMinutes()).padStart(2, '0');
      const time_selected = `${hh}:${mm}`;
      const price = parsePrice(pricingLabelFor(state.selectedFittingType, state.country));
      const { firstName: ec_first_name, lastName: ec_last_name } = splitName(state.fullName);

      const completePayload = {
        location: state.selectedLocation.name,
        fitting_selection: state.selectedFittingType.name,
        date_selected: state.selectedDate,
        time_selected,
        promo_code: state.promoCode.trim() || undefined,
        changed_location:
          confirmEnteredFromLocation.current !== null &&
          confirmEnteredFromLocation.current !== state.selectedLocation.id,
        changed_fitting_type:
          confirmEnteredFromFittingId.current !== null &&
          confirmEnteredFromFittingId.current !== state.selectedFittingType.id,
        confirmation_number: booking.id,
        a_number: booking.id,
        waitlist: 'scheduled' as const,
        value: price,
        currency: countryConfigOrDefault(state.country).currency,
        // Enhanced Conversions user data — passed unhashed to the sGTM
        // Google Ads tag, which hashes server-side. Phone isn't available
        // here (collected post-booking in the GetSetUp form); EC works with
        // email-only as user_data, so this is sufficient for matching.
        email: state.email.trim(),
        first_name: ec_first_name,
        last_name: ec_last_name,
      };

      // Primary conversion event — book_flow_confirm_a_number.
      emitAnalytics({
        event: 'book_flow_confirm_a_number',
        ...completePayload,
        store: state.selectedLocation.name,
        items: [
          {
            item_id: state.selectedFittingType.id,
            item_name: state.selectedFittingType.name,
            item_category: 'fitting',
            price,
            quantity: 1,
          },
        ],
      });

      // Intentional parallel event for ChannelMix attribution pipeline.
      // Enriched with the legacy Web Container ChannelMix tag's parameter
      // shape so attribution fidelity carries through when that tag sunsets.
      const attribution = getAttribution();
      emitAnalytics({
        event: 'channelmix_conversion',
        ...completePayload,
        step: 5,
        step_name: 'booking confirmation - salesforce',
        date_and_time: completePayload.time_selected
          ? `${completePayload.date_selected}T${completePayload.time_selected}`
          : completePayload.date_selected,
        time_zone: state.selectedLocation.timezone,
        referral_method_primary: attribution.utm_source,
        referral_method_secondary: attribution.utm_medium,
        sms_reminder: false,
        waitlisted: completePayload.waitlist,
      });

      setMode('congrats');
    } catch (err) {
      // Message priority: (1) our clean double-booking message when the
      // middleware relays a Salesforce Schedule 409 (it doesn't forward SF's own
      // text); (2) any structured message the middleware does provide — e.g. the
      // slot-taken "Requested slot is not currently available", or SF's message
      // if the middleware is ever fixed to forward it; (3) the raw fallback.
      // A gateway that timed out is not a booking that was refused, and is
      // checked first: the appointment may well exist, and the message has to
      // say so rather than invite a second one.
      const timedOut = gatewayTimeoutMessage(err);
      if (timedOut) bookingTimedOutRef.current = true;

      // A double-booking conflict moments after a timeout is almost certainly
      // the customer's own booking, which did land.
      const conflict = doubleBookingMessage(err);

      const msg =
        timedOut ??
        (conflict && bookingTimedOutRef.current
          ? doubleBookingAfterTimeoutMessage()
          : conflict) ??
        apiErrorMessage(err) ??
        (err instanceof ApiError
          ? // Never echo the body: on a gateway error it is an HTML error page,
            // which used to be printed at the customer, tags and all. The
            // status is dropped too \u2014 CC's Canada review had a customer shown
            // a 500 that actually meant "you already have an appointment", so
            // the number explained nothing and looked like a crash. The most
            // common real cause is named instead.
            'We couldn\u2019t complete this booking. If you already have a fitting ' +
            'scheduled, that may be why. Please try again, or call the studio and ' +
            'they will sort it out.'
          : err instanceof Error
            ? err.message
            : 'Booking failed for an unknown reason.');
      dispatch({ type: 'SUBMIT_ERROR', payload: msg });
      emitAnalytics({ event: 'booking_error', error_message: msg });
    }
  };

  // Post a waitlist request to the middleware when the user dead-ends on
  // "no times available". Same shape as a booking conversion downstream —
  // lands on the congrats screen with a WL- confirmation number instead of
  // an A- number, and emits book_flow_confirm_a_number with waitlist='WL' so the
  // legacy funnel + ChannelMix attribution capture it.
  const handleJoinWaitlist = async () => {
    // The CTA is not rendered where the market has no waitlist (AU); this
    // keeps a stray call from a stale render or a host-page script from
    // posting one anyway.
    if (!waitlistOffered(state.country)) return;
    if (
      !state.selectedLocation ||
      !state.selectedFittingType ||
      !state.selectedDate
    )
      return;

    dispatch({ type: 'WAITLIST_SUBMIT_START' });

    const { firstName, lastName } = splitName(state.fullName);
    const notesParts: string[] = [];
    notesParts.push(`Fitting type: ${state.selectedFittingType.name}`);
    notesParts.push(`Preferred date: ${state.selectedDate}`);
    if (state.promoOptIn) notesParts.push('Customer opted into marketing promos');

    const payload = {
      locationId: state.selectedLocation.id,
      fittingTypeId: state.selectedFittingType.id,
      fittingTypeName: resolveWireName(state.selectedFittingType.name, state.fittingNameMap),
      durationMin: state.selectedFittingType.durationMinutes,
      preferredDate: state.selectedDate,
      customer: {
        firstName,
        lastName,
        email: state.email.trim(),
        // Rides on the booking itself since 2026-10-10. Before, the phone only
        // reached Salesforce through the Step 1 account seed and the
        // post-booking update; the international markets now collect it on
        // the Confirm step, after the seed and before a form the customer may
        // never submit, so the booking is the one write guaranteed to carry
        // it. Undefined where there is none — never an empty string, which
        // would overwrite a number already on the account.
        phone: phoneForBookingApi(state.phone, state.country, state.dialCodeOverride),
      },
      notes: notesParts.join(' · '),
      // The survey answer rides on Schedule, which is the only place
      // Salesforce accepts it — see the note on the confirm step.
      // "How did you hear about us?" no longer travels here. It was folded
      // into this payload only because Salesforce accepted it on Schedule and
      // nowhere else; PostAppointmentUpdate took it on 2026-08-27, so it is
      // asked and sent on the confirmation screen instead. See GetSetUp.tsx.
      marketing: marketingRef.current ?? undefined,
      // Same gate as a booked appointment. A waitlisted customer has completed
      // the funnel, and must not be chased as an abandoner. Null country is the
      // US-default embed, same as the booking payload.
      market: state.country ?? 'us',
    };

    try {
      const waitlist = await createWaitlist(payload);
      dispatch({ type: 'WAITLIST_SUBMIT_SUCCESS', payload: waitlist });
      emit('cc:booking-widget:booking-confirmed', {
        confirmationNumber: waitlist.id,
        bookingId: waitlist.id,
        fittingType: state.selectedFittingType.name,
        location: state.selectedLocation.name,
      });

      // DISABLED 2026-06-18 — see note on the booking branch above.
      // if (waitlist.accountId) setAnalyticsUserId(waitlist.accountId);

      const price = parsePrice(pricingLabelFor(state.selectedFittingType, state.country));
      const { firstName: wl_first_name, lastName: wl_last_name } = splitName(state.fullName);
      const completePayload = {
        location: state.selectedLocation.name,
        fitting_selection: state.selectedFittingType.name,
        date_selected: state.selectedDate,
        // No time selected — waitlist doesn't pin a slot. Send empty string
        // rather than omitting so the legacy date_and_time reconstruction
        // still produces something usable.
        time_selected: '',
        promo_code: state.promoCode.trim() || undefined,
        changed_location:
          confirmEnteredFromLocation.current !== null &&
          confirmEnteredFromLocation.current !== state.selectedLocation.id,
        changed_fitting_type:
          confirmEnteredFromFittingId.current !== null &&
          confirmEnteredFromFittingId.current !== state.selectedFittingType.id,
        confirmation_number: waitlist.id,
        a_number: waitlist.id,
        waitlist: 'WL' as const,
        value: price,
        currency: countryConfigOrDefault(state.country).currency,
        // EC user data (see Schedule path above for rationale).
        email: state.email.trim(),
        first_name: wl_first_name,
        last_name: wl_last_name,
      };

      emitAnalytics({
        event: 'book_flow_confirm_a_number',
        ...completePayload,
        store: state.selectedLocation.name,
        items: [
          {
            item_id: state.selectedFittingType.id,
            item_name: state.selectedFittingType.name,
            item_category: 'fitting',
            price,
            quantity: 1,
          },
        ],
      });

      const waitlistAttribution = getAttribution();
      emitAnalytics({
        event: 'channelmix_conversion',
        ...completePayload,
        step: 5,
        step_name: 'booking confirmation - salesforce',
        date_and_time: completePayload.time_selected
          ? `${completePayload.date_selected}T${completePayload.time_selected}`
          : completePayload.date_selected,
        time_zone: state.selectedLocation.timezone,
        referral_method_primary: waitlistAttribution.utm_source,
        referral_method_secondary: waitlistAttribution.utm_medium,
        sms_reminder: false,
        waitlisted: completePayload.waitlist,
      });

      setMode('congrats');
    } catch (err) {
      const msg =
        apiErrorMessage(err) ??
        (err instanceof ApiError
          ? `Couldn’t join the waitlist (${err.status}). ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Couldn’t join the waitlist right now.');
      dispatch({ type: 'WAITLIST_SUBMIT_ERROR', payload: msg });
      emitAnalytics({ event: 'booking_error', error_message: msg });
    }
  };

  const handleResetWidget = () => {
    // Inline mode always returns to the in-page Step 1 card (never a blank
    // widget); the standard embed returns to the idle/null state.
    setMode(inline ? 'booking' : null);
    dispatch({ type: 'RESET' });
    setLeadCaptureDone(false);
    setHoldExpired(false);
  }

  /**
   * Modal close: in mid-flow modes (booking, confirm-appointment) we
   * preserve state so re-opening resumes where the user left off — closing
   * by accident shouldn't destroy a half-completed selection. In post-write
   * modes (congrats, set-up) closing means "I'm done", so we full-reset.
   */
  const handleCloseModal = () => {
    if (mode === 'congrats') {
      handleResetWidget();
    } else if (inline && rescheduleBoot) {
      // A reschedule mount has no resting card to drop back to — closing the
      // flow closes the whole thing. The host (portal Fittings row, the
      // manage-appointment page) listens for this to reset its own toggle.
      setMode(null);
      setRescheduleClosed(true);
      emit('cc:booking-widget:reschedule-closed', {
        appointment: rescheduleBoot.appointment ?? null,
      });
    } else if (inline) {
      // Closing the promoted modal in inline mode drops back to the in-page
      // Step 1 card rather than hiding the widget entirely.
      setLeadCaptureDone(false);
      setMode('booking');
    } else {
      setMode(null);
    }
  }

  const handleHoldExpired = () => {
    dispatch({ type: 'RESET_PRESERVE_LEAD' });
    setLeadCaptureDone(false);
    setHoldExpired(true);
    setMode('booking');
  }

  // After Get Set Up submission, the BookingConfirmed middle column swaps
  // the GetSetUp form for the "Thanks for helping" content in place — no
  // separate step transition. Just emit the post-booking analytics event.
  useEffect(() => {
    if (state.postBookingComplete && mode === 'congrats') {
      emitAnalytics({
        event: 'post_booking_completed',
        booking_id: state.confirmation?.id ?? 'unknown',
        ball_fitting_interest: state.postBookingBallFitting,
        sms_optin: marketingConsentCombined(state.country)
          ? state.promoOptIn
          : state.postBookingSmsOptIn,
      });
      if (state.postBookingPhone.trim().length > 0) {
        emitAnalytics({ event: 'book_flow_phone_submitted' });
      }
    }
  }, [
    state.postBookingComplete,
    mode,
    state.confirmation,
    state.postBookingBallFitting,
    state.postBookingSmsOptIn,
    state.postBookingPhone,
  ]);

  // In inline mode, Step 1 is the resting card (no progress bar, per design);
  // "expanded" = anything past Step 1, where the panel has grown to cover the
  // page and the progress bar returns.
  const inlineExpanded = inline && !(mode === 'booking' && !leadCaptureDone);

  // Everything Step 1 does on completion — advance to Step 2, write the lead to
  // the vault + Salesforce, remember the visitor, and fire the funnel's Step
  // 1→2 analytics (book_flow_submit + generate_lead, plus marketing_opt_in).
  // Shared between the seed card's Continue button and the location-context
  // fast-forward, so a visitor sent straight to Step 2 emits the exact same
  // events and performs the same side effects as one who clicked Continue.
  const completeLeadCapture = () => {
    setHoldExpired(false);
    setLeadCaptureDone(true);
    emit('cc:booking-widget:step-changed', { step: 'lead-capture' });
    postLead({
      name: state.fullName.trim(),
      email: state.email.trim(),
      phone: state.phone.trim() || undefined,
      store: state.selectedLocation?.name,
    });
    {
      const seedName = splitName(state.fullName.trim());
      seedAccount({
        firstName: seedName.firstName,
        lastName: seedName.lastName,
        email: state.email.trim(),
        store: state.selectedLocation?.name,
        phone: state.phone.trim() || undefined,
        emailOptIn: state.promoOptIn,
        // AU's single marketing box drives both opt-ins, so SMS follows the
        // email box; other markets keep their own SMS control. Must not be
        // omitted — Salesforce treats a missing smsOptIn as consent.
        smsOptIn: marketingConsentCombined(state.country)
          ? state.promoOptIn
          : state.postBookingSmsOptIn,
        sessionId: marketingRef.current?.submissionId,
        market: state.country ?? 'us',
      });
    }
    writeIdentity({
      fullName: state.fullName.trim(),
      email: state.email.trim(),
      storeId: state.selectedLocation?.id,
      storeName: state.selectedLocation?.name,
      source: 'booking',
    });
    // Fire BOTH events at lead capture per Donald's spec.
    emitAnalytics({
      event: 'book_flow_submit',
      account_status: 'created', // refined by middleware response on book_flow_confirm_a_number
      form_id: 'cc-booking-widget',
      form_name: 'CC Booking Widget',
    });
    emitAnalytics({
      event: 'generate_lead',
      value: 0,
      currency: countryConfigOrDefault(state.country).currency,
      lead_source: window.location.pathname || 'unknown',
    });
    if (state.promoOptIn) {
      emitAnalytics({
        event: 'marketing_opt_in',
        // AU's single box opts into both channels at once; elsewhere Step 1
        // captures email consent only (SMS is its own later checkbox).
        opt_in_channels: marketingConsentCombined(state.country)
          ? ['email', 'sms']
          : ['email'],
        source_step: 'contact_info',
      });
    }
  };
  // Latest closure, callable from effects (the fast-forward) without stale state.
  const completeLeadCaptureRef = useRef(completeLeadCapture);
  completeLeadCaptureRef.current = completeLeadCapture;
  // Reschedule lookup matched several upcoming appointments — swap Step 1's
  // form for the picker until one is chosen (originalAppointment set) or the
  // user backs out (candidates cleared).
  const showReschedulePicker =
    state.rescheduleMode &&
    (state.rescheduleCandidates?.length ?? 0) > 1 &&
    !state.originalAppointment;

  const content = (
    <>
      {showProgress && !(inline && !inlineExpanded) && <StepProgress step={currentStep} />}
      <div ref={setRoot} className={styles.root} data-cc-layout={isDesktop ? 'desktop' : 'mobile'}>
        {isReadOnly() && (
          <div className={styles.readOnlyBanner}>
            Read-only preview · production data, no bookings will be created
          </div>
        )}
        {mode === 'booking' && !leadCaptureDone && showReschedulePicker && (
          <RescheduleAppointmentPicker
            candidates={state.rescheduleCandidates ?? []}
            country={state.country}
            onSelect={(appt) => void hydrateFromAppointment(appt)}
            onBack={() => dispatch({ type: 'ENTER_RESCHEDULE_MODE' })}
          />
        )}

        {/* A rescheduleBoot mount looks its appointment up before showing
            anything — a Step-1 form that flashes and then vanishes reads as
            broken. Hold a loading card until the lookup lands; an error falls
            back to the editable Step-1 form below, and a multi-match shows
            the picker above. */}
        {mode === 'booking' && !leadCaptureDone && !showReschedulePicker
          && rescheduleBoot && !state.rescheduleLookupError && (
          <div className={styles.idle}>
            <p><span className={styles.spinner} /> Pulling up your appointment…</p>
          </div>
        )}

        {mode === 'booking' && !leadCaptureDone && !showReschedulePicker
          && !(rescheduleBoot && !state.rescheduleLookupError) && (
          <LeadCapture
            state={state}
            dispatch={dispatch}
            variant={inline ? (inlineVariant === 'card' ? 'card' : 'inline') : (ctxSeedCard ? 'card' : 'modal')}
            cardHeading={cardHeading}
            cardWide={cardWide}
            cardBasic={cardBasic}
            phoneMandatory={phoneMandatoryState}
            showExpiredNotice={holdExpired}
            // A location/fitter context adds a fifth Step-1 field, FITTER: a
            // fitter CTA preselects it and the visitor still picks a fitting type
            // before Step 2. Only where a studio is forced (the store hero card
            // or a fitter modal); the plain embed keeps its four fields.
            showFitter={ctxSeedCard || !!presetStoreName}
            // The reschedule entry only on the mount that exists for it.
            allowReschedule={!!rescheduleBoot}
            onContinue={() => {
              // Snapshot the in-flow card's rect so Step 2 grows out of it.
              // Taken for BOTH paths (booking + reschedule) — a successful
              // reschedule hydration also promotes past Step 1, and the FLIP
              // grow needs the resting card's rect either way.
              if (inline && inlineCardRef.current) {
                const r = inlineCardRef.current.getBoundingClientRect();
                growRectRef.current = { top: r.top, left: r.left, width: r.width, height: r.height };
              }
              // Reschedule mode: Step 1 is an identity lookup, not a lead.
              // No lead vault write, no Account seed, no funnel analytics —
              // the customer already exists in every one of those systems.
              if (state.rescheduleMode) {
                void handleRescheduleLookup();
                return;
              }
              // Everything else Step 1 does lives in completeLeadCapture, shared
              // with the location-context fast-forward. It runs exactly once
              // here: an earlier version called it and then repeated its lead
              // vault + Account seed calls inline, posting every lead twice.
              completeLeadCapture();
            }}
          />
        )}

        {mode === 'booking' && leadCaptureDone && (
          <BookingStripPanel
            state={state}
            dispatch={dispatch}
            onJoinWaitlist={handleJoinWaitlist}
            onEditLead={() => setLeadCaptureDone(false)}
            onBook={() => {
              // Snapshot the values entering confirm so we can detect
              // "user changed something on the review page" later.
              confirmEnteredFromLocation.current = state.selectedLocation?.id ?? null;
              confirmEnteredFromFittingId.current = state.selectedFittingType?.id ?? null;
              // Pre-confirmation snapshot per Donald's schema.
              if (
                state.selectedLocation &&
                state.selectedFittingType &&
                state.selectedDate &&
                state.selectedSlot
              ) {
                const slotStart = new Date(state.selectedSlot.start);
                const hh = String(slotStart.getHours()).padStart(2, '0');
                const mm = String(slotStart.getMinutes()).padStart(2, '0');
                emitAnalytics({
                  event: 'book_flow_appointment',
                  location: state.selectedLocation.name,
                  fitting_selection: state.selectedFittingType.name,
                  date_selected: state.selectedDate,
                  time_selected: `${hh}:${mm}`,
                  value: parsePrice(pricingLabelFor(state.selectedFittingType, state.country)),
                  currency: countryConfigOrDefault(state.country).currency,
                });
              }
              dispatch({ type: 'CLEAR_SUBMIT_ERROR' });
              setMode('confirm-appointment');
            }}
            layoutType={isDesktop ? 'desktop' : 'mobile'}
          />
        )}

        {mode === 'confirm-appointment' && (
          <ConfirmAppointment
            state={state}
            dispatch={dispatch}
            phoneMandatory={phoneMandatoryState}
            onConfirm={state.rescheduleMode ? handleConfirmReschedule : handleConfirmBooking}
            onEditField={(field: BookingEditField) => {
              for (const action of bookingEditFromConfirmActions(field)) {
                dispatch(action);
              }
              setMode('booking');
              // Lead-edit (Name / Email) drops the user back at the
              // LeadCapture step instead of the booking strip; the
              // strip's other Change actions stay on the strip.
              if (field === 'lead') setLeadCaptureDone(false);
              // Bring the booking strip back into view. The Change buttons sit
              // mid/lower on the confirm screen; without an explicit scroll
              // the user's viewport stays where it was and the now-empty
              // field at the top of the strip is off-screen, making the
              // screen change feel like nothing happened.
              //
              // Use requestAnimationFrame so the new render has committed and
              // the rootRef position is accurate, then window.scrollTo using
              // the root's absolute Y in the document (scrollIntoView is a
              // no-op when the element is already "in view" by any amount —
              // which happens when the widget spans the viewport height).
              requestAnimationFrame(() => {
                const el = rootRef.current;
                if (!el) return;
                const targetY = window.scrollY + el.getBoundingClientRect().top - 20;
                window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
              });
            }}
            onHoldExpired={handleHoldExpired}
          />
        )}

        {mode === 'congrats' && (
          <BookingConfirmed state={state}>
            {/* GetSetUp is a first-booking upsell (pre-fit call, ball fitting,
                SMS) — a customer moving an existing appointment already gave
                all of that, so the rescheduled variant renders without it. */}
            {!state.rescheduleMode && (
              <GetSetUp
                state={state}
                dispatch={dispatch}
                // "Already collected" — on Step 1 in the mandatory flavour, or
                // on the Confirm step in the international markets. Either
                // way the post-booking form must not ask a third time.
                phoneMandatory={
                  phoneMandatoryState
                  || collectsPhoneOnConfirm(state.country, phoneMandatoryState, state.rescheduleMode)
                }
              />
            )}
          </BookingConfirmed>
        )}

        {/* Modal exposes its own close affordance (top-right ×) so the
            in-flow close button is redundant. Removed to avoid duplicate
            close UI inside the modal. */}
      </div>
    </>
  );

  // Inline landing-page mode renders a single fixed panel (portaled to body)
  // that MORPHS from a Step-1 card in the bottom-right corner into a large
  // inset panel at Step 2+ — it's the same element transitioning its geometry,
  // so it visually "expands to cover" the page's left content while the
  // top-left logo stays clear. All downstream steps render inside this panel;
  // the standard button-triggered modal path below is left untouched.
  if (inline) {
    if (rescheduleClosed) return null;

    // Step 1 (resting card): renders IN-FLOW inside the host's mount container
    // — no position:fixed, no body portal — so it sits within the page
    // section's <div> exactly where the host placed it, on BOTH desktop and
    // mobile. (The previous desktop build pinned a fixed, viewport-sized panel
    // to <body>, which is the reported "widget is stuck / not inside the div"
    // bug.) Steps 2-4 still expand to a full overlay: a standard centered modal
    // on desktop, and a "bare" (scrim-less) modal on mobile so the landing
    // background peeks through the thin margin. The inner components already
    // render their mobile layouts below 1024px via isDesktop/layoutType.
    if (!inlineExpanded) {
      return (
        <>
          <div className={styles.inlineCard} ref={inlineCardRef}>{content}</div>
          {ownsTrafficDrawer && shouldShowTrafficDrawer() && <TrafficDrawer />}
        </>
      );
    }
    return (
      <>
        <Modal
          isOpen={mode !== null}
          onClose={handleCloseModal}
          variant={isDesktop ? 'default' : 'bare'}
          contentStyle={isDesktop ? modalContentStyle : undefined}
          growFromRect={isDesktop ? growRectRef.current : null}
        >
          {content}
        </Modal>
        {ownsTrafficDrawer && shouldShowTrafficDrawer() && <TrafficDrawer />}
      </>
    );
  }

  return (
    <>
      <Modal isOpen={mode !== null} onClose={handleCloseModal} contentStyle={modalContentStyle}>
        {content}
      </Modal>
      {ownsTrafficDrawer && shouldShowTrafficDrawer() && <TrafficDrawer />}
    </>
  );
}

/**
 * Developer affordance — only show the API traffic drawer outside the
 * production host. Strict allowlist: the drawer renders ONLY on
 * booking-stg.clubchampion.com (the staging demo host) and local
 * development (localhost / 127.0.0.1). No config override — a customer
 * page must be structurally unable to show it.
 *
 * History: this used to be a prod-hostname denylist that leaked when
 * account.clubchampion.com launched (any new customer-facing hostname
 * fell through to "not prod, show it"). An allowlist cannot rot into
 * showing on a page it hasn't heard of.
 */
function shouldShowTrafficDrawer(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return (
    host === 'booking-stg.clubchampion.com' ||
    host === 'localhost' ||
    host === '127.0.0.1'
  );
}



function splitName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === '') return { firstName: 'Guest', lastName: '(none)' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '(none)' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}
